import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type { RequestGroup } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import type {
  AOEAnswerCoding,
  AOEQuestionAnswerType,
} from "./useRetrieveAOEForOrder";

interface SaveAOEParams {
  questionnaireResponseId: string;
  answers: {
    linkId: string;
    answerValues: {
      answerType: AOEQuestionAnswerType;
      answerValue: string | AOEAnswerCoding;
    }[];
  }[];
}

interface SaveAOEResponse {
  requestGroup: RequestGroup;
}

export const useSaveAOE = ({
  patientId,
  encounterId,
  requestGroupId,
}: {
  patientId: string;
  encounterId: string;
  requestGroupId: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<SaveAOEResponse, SaveAOEParams>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.Labs.SAVE_AOE({
        patientId,
        encounterId,
        requestGroupId,
      }),
      mutationFn: (params) => {
        return axiosInstance.put(
          `/labs/order/aoe/${params.questionnaireResponseId}`,
          {
            answers: params.answers,
          },
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT({
            patientId,
            encounterId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT_V2({
            patientId,
            encounterId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.SUMMARY({
            patientId,
            encounterId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.AOE_FOR_ORDER({
            patientId,
            requestGroupId,
          }),
        });
      },
    },
  });
};
